import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProductByHandle } from "@/lib/shopify";
import { Button } from "@/components/ui/button";
import { useCartStore } from "@/stores/cartStore";
import { Loader2, ShoppingCart, ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/utils";
import Navigation from "@/components/Navigation";
import { logger } from "@/lib/logger";
import ProductImageGallery from "@/components/ProductImageGallery";

import { getProductExtras } from "@/data/productExtras";
import { WhatYouWillLearnSection } from "@/components/WhatYouWillLearnSection";
import { WhatsIncludedSection } from "@/components/WhatsIncludedSection";
import { WhoThisIsForSection } from "@/components/WhoThisIsForSection";
import { ResultsSection } from "@/components/ResultsSection";
import { TestimonialSection } from "@/components/TestimonialSection";
import { FAQSection } from "@/components/FAQSection";
import PageSeo from "@/components/PageSeo";
import { useLanguage } from "@/i18n/LanguageContext";


export default function ProductDetail() {
  const { t } = useLanguage();
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore(state => state.addItem);
  
  // Get product-specific extras
  const productExtras = handle ? getProductExtras(handle) : null;

  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;
      
      try {
        const data = await fetchProductByHandle(handle);
        setProduct(data);
        if (data?.variants?.edges?.[0]) {
          setSelectedVariant(data.variants.edges[0].node);
        }
      } catch (error) {
        logger.error('Error loading product:', error);
        toast.error('Product not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [handle, navigate]);

  const handleAddToCart = () => {
    if (product && selectedVariant) {
      addItem({
        product: { node: product },
        variantId: selectedVariant.id,
        variantTitle: selectedVariant.title,
        price: selectedVariant.price,
        quantity: quantity,
        selectedOptions: selectedVariant.selectedOptions || []
      });
      toast.success(t("product.addedToCartTitle"), {
        description: `${quantity} × ${product.title}`,
      });
    }
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const images = product.images?.edges?.map((edge: any) => edge.node) || [];
  
  // Check if product is bookable (has consulting/advisory/call tags)
  const productTags = product.tags || [];
  const isBookableProduct = productTags.some((tag: string) => 
    ['consulting', 'advisory', 'call'].includes(tag.toLowerCase())
  );
  
  const displayTitle = handle === 'founder-consulting-package' ? 'Founding Team Sprint' : product.title;
  const rawDesc = (product.description || '').replace(/\s+/g, ' ').trim();
  const metaDescription = (rawDesc.slice(0, 155) || `${displayTitle} — advisory package from Peter Dingle.`) + (rawDesc.length > 155 ? '…' : '');
  const productImage = images[0]?.url;
  const price = selectedVariant?.price?.amount;
  const currency = selectedVariant?.price?.currencyCode || 'NZD';
  const faqSchema = productExtras?.faqItems?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: productExtras.faqItems.map((f: { question: string; answer: string }) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: displayTitle,
    description: rawDesc || displayTitle,
    image: productImage,
    brand: { "@type": "Brand", name: "Peter M Dingle" },
    ...(price
      ? {
          offers: {
            "@type": "Offer",
            price,
            priceCurrency: currency,
            availability: selectedVariant?.availableForSale
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            url: `https://petermdingle.com/product/${handle}`,
          },
        }
      : {}),
  };

  return (
    <div className="min-h-screen bg-background">
      <PageSeo
        title={`${displayTitle} | Peter Dingle`}
        description={metaDescription}
        path={`/product/${handle}`}
        type="product"
        image={productImage}
        jsonLd={faqSchema ? [productSchema, faqSchema] : productSchema}
      />
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24 max-w-7xl">

        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("product.backToStore")}
        </Button>

        {/* Hero Section: Image Gallery + Content */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Image Gallery */}
          <div>
            <ProductImageGallery images={images} productTitle={product.title} />
          </div>

          {/* Product Content */}
          <div className="flex flex-col space-y-6">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                {handle === 'founder-consulting-package' ? 'Founding Team Sprint' : product.title}
              </h1>
              <p className="text-4xl font-bold text-primary mb-6">
                {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
              </p>
            </div>

            {product.descriptionHtml ? (
              <div
                className="product-description prose prose-neutral max-w-none text-base leading-relaxed text-foreground/80 prose-headings:font-semibold prose-headings:text-foreground prose-p:my-4 prose-ul:my-4 prose-li:my-1 prose-hr:my-8 prose-strong:text-foreground"
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
              />
            ) : (
              <div className="prose prose-neutral max-w-none text-base leading-relaxed text-foreground/80">
                {product.description.split('\n').map((paragraph: string, index: number) =>
                  paragraph.trim() ? <p key={index}>{paragraph}</p> : null
                )}
              </div>
            )}

            {/* Variant Selection */}
            {product.options && product.options.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                {product.options
                  .filter((option: any) => !(option.name === "Title" && option.values.length === 1 && option.values[0] === "Default Title"))
                  .map((option: any) => (
                  <div key={option.name}>
                    <label className="block text-sm font-semibold mb-3 uppercase tracking-wide">
                      {option.name}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {option.values.map((value: string) => {
                        const variant = product.variants.edges.find((v: any) =>
                          v.node.selectedOptions.some(
                            (o: any) => o.name === option.name && o.value === value
                          )
                        );
                        const isSelected = selectedVariant?.selectedOptions.some(
                          (o: any) => o.name === option.name && o.value === value
                        );

                        return (
                          <Button
                            key={value}
                            variant={isSelected ? "default" : "outline"}
                            onClick={() => variant && setSelectedVariant(variant.node)}
                          >
                            {value}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Booking Section for Consulting Products */}
            {isBookableProduct && (
              <div className="pt-6 border-t">
                <div className="border rounded-lg p-6 bg-secondary/10">
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold">{t("product.bookCallTitle")}</h2>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    {t("product.bookCallDescription")}
                  </p>
                  <Button
                    variant="accent"
                    size="lg"
                    onClick={() => window.open("https://calendar.app.google/smFTnDe7izrtAPVg6", "_blank")}
                    className="w-full font-sans"
                  >
                    {t("product.bookCallButton")}
                  </Button>
                </div>
              </div>
            )}

            {/* Quantity Selector + Add to Cart */}
            <div className="space-y-4 pt-4">
              {isBookableProduct && (
                <p className="text-sm text-muted-foreground italic">
                  {t("product.bookingReminder")}
                </p>
              )}
              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold uppercase tracking-wide">{t("product.quantity")}</label>
                <div className="flex items-center border rounded-md">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={decrementQuantity}
                    className="h-10 w-10"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="px-6 font-semibold">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={incrementQuantity}
                    className="h-10 w-10"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Button
                onClick={handleAddToCart}
                size="xl"
                className="w-full sticky bottom-4 z-10 shadow-lg"
                disabled={!selectedVariant?.availableForSale}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {selectedVariant?.availableForSale ? t("product.addToCart") : t("product.outOfStock")}
              </Button>
            </div>
          </div>
        </div>

        {/* Below the Fold: Dynamic Product-Specific Sections */}
        <div className="max-w-4xl mx-auto">
          {productExtras?.whatYouWillLearn && (
            <WhatYouWillLearnSection items={productExtras.whatYouWillLearn} />
          )}

          {productExtras?.whatsIncluded && (
            <WhatsIncludedSection items={productExtras.whatsIncluded} />
          )}

          {productExtras?.whoThisIsFor && (
            <WhoThisIsForSection description={productExtras.whoThisIsFor} />
          )}

          {productExtras?.resultsYouCanExpect && (
            <ResultsSection items={productExtras.resultsYouCanExpect} />
          )}

          {productExtras?.testimonial && (
            <TestimonialSection
              quote={productExtras.testimonial.quote}
              authorName={productExtras.testimonial.authorName}
              authorTitle={productExtras.testimonial.authorTitle}
            />
          )}

          {productExtras?.faqItems && (
            <FAQSection items={productExtras.faqItems} />
          )}
        </div>
      </div>
    </div>
  );
}
